import { CommonModule } from "@angular/common";
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { RouterLink } from "@angular/router";
import { Subscription } from "rxjs";

import { guardarDescarga, mensajeDeDescarga } from "src/app/bsc/bsc-descarga";
import { CalidadCodigoRes, ReporteEmitido } from "src/app/shared/model/calidad-codigo/calidad-codigo.model";
import { CalidadCodigoService } from "../calidad-codigo.service";
import { TIPO_REPORTE, textoDeError } from "../cc-estados";
import { entero, fechaHora, huellaCorta, nombres, plural } from "../cc-formato";
import { ajustePaginado, cuentaEstable, numerosDePagina, textoRango } from "../cc-paginado";
import { avisoDeEmision } from "../reporte/actualizacion-cc";
import { descargarPdfProyecto } from "../reporte/pdf-proyecto";
import { descargarPdfQa } from "../reporte/pdf-qa";
import { FlotantesCcService } from "../flotantes/flotantes-cc.service";
import { DialogoReporteQaComponent, abrirDialogoReporteQa } from "./dialogo-reporte-qa.component";

type EstadoBandeja = "CARGANDO" | "DATOS" | "VACIO" | "ERROR";

/** Un renglón de la bandeja, armado UNA vez por respuesta. */
interface FilaReporte {
  id: number;
  folio: string;
  tipo: string;
  tipoClase: string;
  proyectos: string;
  proyectosCompletos: string;
  emitidoPor: string;
  fecha: string;
  huella: string;
  huellaCompleta: string;
  analisis: string;
  etiquetaPdf: string;
  etiquetaExcel: string;
}

const RUTA_TABLERO = "/admin/calidad-codigo/tablero";
const FILAS_MIN = 4;
const FILAS_MAX = 15;
const ALTO_FILA = 50;
/** El aire bajo la tarjeta (relleno de la página y borde), en píxeles. */
const MARGEN_INFERIOR = 30;

/**
 * Calidad de código · Reportes (docs/CALIDAD_CODIGO.md §5): la bandeja de
 * folios emitidos (de un proyecto, CC-…, o para QA, QA-…) con quién los
 * emitió, cuándo y su huella. Cada folio se vuelve a bajar idéntico: el PDF
 * se arma en el navegador con la foto congelada (reporteDatos.do) y el Excel
 * lo arma el servidor (reporteExcel.do). «Nuevo reporte para QA» abre el
 * mismo cuadro que el tablero.
 *
 * Base visual: hero, tabla y paginado de table-page (src/styles.css) con
 * los tokens del módulo. Las filas por página se calculan con la altura de
 * la ventana; la lista viene paginada del servidor, más reciente primero.
 */
@Component({
  selector: "app-reportes-cc",
  standalone: true,
  templateUrl: "./reportes-cc.component.html",
  styleUrls: ["../calidad-codigo.shared.css", "./reportes-cc.component.css"],
  imports: [CommonModule, RouterLink, MatButtonModule],
})
export class ReportesCcComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly rutaTablero = RUTA_TABLERO;
  @ViewChild("tabla") private readonly tabla?: ElementRef<HTMLElement>;

  estado: EstadoBandeja = "CARGANDO";
  consultando = false;
  error = "";
  filas: FilaReporte[] = [];
  total = 0;
  pagina = 1;
  porPagina = 8;
  /** El aire que se reparte en cada fila para que la tarjeta llegue al final de la ventana. */
  extraFila = 0;
  puedeReportar = false;
  /** El folio que se está bajando y en qué formato. */
  bajando: { folio: string; formato: "PDF" | "EXCEL" } | null = null;

  private consulta?: Subscription;
  private accion?: Subscription;
  private esperaAjuste: ReturnType<typeof setTimeout> | null = null;
  private dialogo?: MatDialogRef<DialogoReporteQaComponent, ReporteEmitido>;
  /** Cuántas veces seguidas cambiaron las filas por página (tope: 3) y si la última vez se redujeron. */
  private ajustesSeguidos = 0;
  private ajusteEncogio = false;

  constructor(
    private readonly servicio: CalidadCodigoService,
    private readonly dialog: MatDialog,
    private readonly cdr: ChangeDetectorRef,
    private readonly flotantes: FlotantesCcService,
  ) {}

  ngOnInit(): void {
    this.consultar();
  }

  ngAfterViewInit(): void {
    this.programaAjuste();
  }

  ngOnDestroy(): void {
    this.consulta?.unsubscribe();
    this.accion?.unsubscribe();
    if (this.esperaAjuste) {
      clearTimeout(this.esperaAjuste);
    }
    this.dialogo?.close();
  }

  /* ============================ Consulta ============================ */

  consultar(): void {
    this.consulta?.unsubscribe();
    this.consultando = true;
    this.error = "";
    if (this.filas.length === 0) {
      this.estado = "CARGANDO";
    }
    this.cdr.detectChanges();
    this.consulta = this.servicio.reportes(this.pagina, this.porPagina).subscribe({
      next: (res) => {
        this.consultando = false;
        if (res?.codigoError) {
          this.estado = "ERROR";
          this.error = textoDeError(res, "consultar los reportes");
          this.filas = [];
        } else {
          this.aplica(res);
        }
        this.cdr.detectChanges();
        this.programaAjuste();
      },
      error: () => {
        this.consultando = false;
        this.estado = "ERROR";
        this.error = "No se pudieron consultar los reportes: sin conexión con el servidor del COS.";
        this.filas = [];
        this.cdr.detectChanges();
      },
    });
  }

  private aplica(res: CalidadCodigoRes): void {
    this.puedeReportar = res.puedeReportar === true;
    const r = res.reportes;
    this.total = r?.total ?? 0;
    this.filas = (r?.lista ?? []).map((x) => this.fila(x));
    this.estado = this.filas.length > 0 ? "DATOS" : "VACIO";
  }

  private fila(r: ReporteEmitido): FilaReporte {
    const proyectos = r.proyectos ?? [];
    return {
      id: r.id,
      folio: r.folio,
      tipo: TIPO_REPORTE[r.tipo] ?? r.tipo,
      tipoClase: r.tipo === "QA" ? "is-info" : "is-neutro",
      proyectos: nombres(proyectos),
      proyectosCompletos: proyectos.join(" · "),
      emitidoPor: r.emitidoPor || "—",
      fecha: fechaHora(r.fecha),
      huella: huellaCorta(r.huella),
      huellaCompleta: r.huella,
      analisis: r.analisisIncluidos != null ? `${entero(r.analisisIncluidos)} análisis` : "",
      etiquetaPdf: `Bajar el PDF del folio ${r.folio}`,
      etiquetaExcel: `Bajar el Excel del folio ${r.folio}`,
    };
  }

  /* ============================ Acciones ============================ */

  /** El PDF se vuelve a armar con la foto congelada del folio, idéntico al original. */
  pdf(f: FilaReporte): void {
    if (this.bajando) {
      return;
    }
    this.bajando = { folio: f.folio, formato: "PDF" };
    this.cdr.detectChanges();
    this.accion?.unsubscribe();
    this.accion = this.servicio.reporteDatos(f.folio).subscribe({
      next: (res) => {
        this.bajando = null;
        if (res?.codigoError || !res?.reporte) {
          this.flotantes.error(textoDeError(res, `bajar el folio ${f.folio}`));
          this.cdr.detectChanges();
          return;
        }
        if (res.reporte.tipo === "QA") {
          descargarPdfQa(res.reporte);
        } else {
          descargarPdfProyecto(res.reporte);
        }
        this.flotantes.exito(`PDF del folio ${f.folio} descargado.`);
        this.cdr.detectChanges();
      },
      error: () => {
        this.bajando = null;
        this.flotantes.error("No se pudo bajar el reporte: sin conexión con el servidor del COS.");
        this.cdr.detectChanges();
      },
    });
  }

  excel(f: FilaReporte): void {
    if (this.bajando) {
      return;
    }
    this.bajando = { folio: f.folio, formato: "EXCEL" };
    this.cdr.detectChanges();
    this.accion?.unsubscribe();
    this.accion = this.servicio.reporteExcel(f.folio).subscribe({
      next: (res) => {
        this.bajando = null;
        guardarDescarga(res, `${f.folio}.xlsx`);
        this.flotantes.exito(`Excel del folio ${f.folio} descargado.`);
        this.cdr.detectChanges();
      },
      error: (err: unknown) => {
        void mensajeDeDescarga(err, "bajar el Excel").then((mensaje) => {
          this.bajando = null;
          this.flotantes.error(mensaje);
          this.cdr.detectChanges();
        });
      },
    });
  }

  /**
   * «Nuevo reporte para QA»: el cuadro consulta el tablero completo; al emitir (el servidor corre antes una pasada de
   * cada proyecto en SonarQube), el PDF se descarga, el flotante dice cómo le fue a esa consulta (actualizacion-cc.ts)
   * y la bandeja se refresca.
   */
  nuevoQa(): void {
    if (this.dialogo) {
      return;
    }
    this.dialogo = abrirDialogoReporteQa(this.dialog, {});
    this.dialogo.afterClosed().subscribe((reporte) => {
      this.dialogo = undefined;
      if (!reporte) {
        return;
      }
      descargarPdfQa(reporte);
      this.pagina = 1;
      const proyectos = plural((reporte.proyectos ?? []).length, "proyecto", "proyectos");
      const aviso = avisoDeEmision(`Reporte ${reporte.folio} para QA (${proyectos})`, reporte);
      this.flotantes[aviso.tipo](aviso.texto);
      this.consultar();
    });
  }

  estaBajando(f: FilaReporte, formato: "PDF" | "EXCEL"): boolean {
    return this.bajando?.folio === f.folio && this.bajando.formato === formato;
  }

  /* ============================ Paginado ============================ */

  get paginas(): number[] {
    return numerosDePagina(this.total, this.porPagina);
  }

  get textoRango(): string {
    return textoRango(this.total, this.pagina, this.porPagina, "reporte", "reportes");
  }

  irA(pagina: number): void {
    const destino = Math.min(Math.max(1, pagina), this.paginas.length);
    if (destino === this.pagina) {
      return;
    }
    this.pagina = destino;
    this.consultar();
  }

  @HostListener("window:resize")
  alCambiarTamano(): void {
    this.ajustesSeguidos = 0;
    this.ajusteEncogio = false;
    this.programaAjuste();
  }

  private programaAjuste(): void {
    if (this.esperaAjuste) {
      clearTimeout(this.esperaAjuste);
    }
    this.esperaAjuste = setTimeout(() => {
      this.esperaAjuste = null;
      this.ajustaFilas();
    }, 60);
  }

  /**
   * Las filas que caben hasta el final de la ventana y el aire que se reparte
   * en cada una. Si cambia el aire se repinta y se mide una vez más; si
   * cambian las filas, se vuelve a pedir la página. Las filas por página solo
   * cambian estando en la primera (en otra página con renglones más altos se
   * ajusta el aire, no el tamaño: si no, brincaría de página) y como mucho
   * tres veces seguidas, por si dos páginas se alternan.
   */
  private ajustaFilas(pasadas = 2): void {
    if (!this.tabla) {
      return;
    }
    const ajuste = ajustePaginado(this.tabla.nativeElement, MARGEN_INFERIOR, FILAS_MIN, FILAS_MAX, ALTO_FILA, this.total);
    const porPagina = cuentaEstable(this.porPagina, ajuste.porPagina, this.ajusteEncogio);
    if (porPagina !== this.porPagina && this.pagina === 1 && this.ajustesSeguidos < 3) {
      this.ajustesSeguidos++;
      this.ajusteEncogio = porPagina < this.porPagina;
      this.porPagina = porPagina;
      this.consultar();
      return;
    }
    if (ajuste.extraFila !== this.extraFila) {
      this.extraFila = ajuste.extraFila;
      this.cdr.detectChanges();
      if (pasadas > 0) {
        this.esperaAjuste = setTimeout(() => {
          this.esperaAjuste = null;
          this.ajustaFilas(pasadas - 1);
        }, 60);
      }
    }
  }

  trackFila(_: number, f: FilaReporte): number {
    return f.id;
  }
}
